import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

function LocationPicker({ location, setLocation }) {
  useMapEvents({
    click(e) {
      setLocation({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });

  return location.lat && location.lng ? (
    <Marker position={[location.lat, location.lng]} />
  ) : null;
}


function MapFlyTo({ location }) {
  const map = useMapEvents({});
  useEffect(() => {
    if (location.lat && location.lng) {
      map.setView([location.lat, location.lng], 16);
    }
  // eslint-disable-next-line
  }, [location.lat, location.lng]);
  return null;
}

export default function AddressForm({ initial = {}, onSubmit, onCancel }) {
  const [form, setForm] = useState({
    label: initial.label || "",
    address: initial.address || "",
    city: initial.city || "",
    isDefault: initial.isDefault || false,
    location: initial.location || {
      lat: initial.lat || "",
      lng: initial.lng || "",
    },
  });
  const [locating, setLocating] = useState(false);

  const handleChange = (field, value) => setForm({ ...form, [field]: value });

  const useDeviceLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Tu navegador no soporta geolocalización");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        setForm((prev) => ({ ...prev, location: { lat, lng } }));
        // Reverse geocode with Nominatim to pre-fill address
        try {
          const r = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&accept-language=es`
          );
          const data = await r.json();
          const addr =
            data.address?.road
              ? `${data.address.road}${data.address.house_number ? " " + data.address.house_number : ""}`
              : data.display_name?.split(",")[0] || "";
          const city =
            data.address?.city ||
            data.address?.town ||
            data.address?.village ||
            data.address?.municipality ||
            "";
          setForm((prev) => ({
            ...prev,
            address: prev.address || addr,
            city:    prev.city    || city,
            location: { lat, lng },
          }));
        } catch {
          // reverse geocode failed — location is still set
        }
        setLocating(false);
      },
      (err) => {
        const msgs = {
          1: "Permiso de ubicación denegado. Actívalo en la configuración de tu navegador.",
          2: "No se pudo obtener la ubicación. Intenta de nuevo.",
          3: "Tiempo agotado obteniendo ubicación.",
        };
        toast.error(msgs[err.code] || "Error obteniendo ubicación");
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!form.address || form.address.length < 5) {
      toast.error("La dirección debe tener al menos 5 caracteres");
      return;
    }
    if (!form.city || form.city.length < 2) {
      toast.error("Ingresa el nombre de la ciudad");
      return;
    }
    if (!form.location.lat) {
      toast.warning("Selecciona tu ubicación en el mapa");
      return;
    }

    onSubmit(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div>
        <label className="text-sm text-gray-700">Etiqueta</label>
        <input
          className="w-full mt-1 p-3 border rounded-xl"
          value={form.label}
          onChange={(e) => handleChange("label", e.target.value)}
          placeholder="Casa / Trabajo"
        />
      </div>

      <div>
        <label className="text-sm text-gray-700">
          Dirección <span className="text-red-500">*</span>
        </label>
        <input
          className="w-full mt-1 p-3 border rounded-xl"
          value={form.address}
          onChange={(e) => handleChange("address", e.target.value)}
          placeholder="Calle 10 # 5-12"
          required
        />
      </div>

      <div>
        <label className="text-sm text-gray-700">
          Ciudad <span className="text-red-500">*</span>
        </label>
        <input
          className="w-full mt-1 p-3 border rounded-xl"
          value={form.city}
          onChange={(e) => handleChange("city", e.target.value)}
          placeholder="Cúcuta"
          required
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-sm text-gray-700">
            Ubicación en el mapa <span className="text-red-500">*</span>
          </label>
          <button
            type="button"
            onClick={useDeviceLocation}
            disabled={locating}
            className="flex items-center gap-1.5 text-xs font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 px-2.5 py-1 rounded-lg transition disabled:opacity-60"
          >
            {locating ? (
              <span className="inline-block w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
            ) : (
              <span>📍</span>
            )}
            {locating ? "Obteniendo..." : "Usar mi ubicación"}
          </button>
        </div>
        <MapContainer
          center={
            form.location.lat
              ? [form.location.lat, form.location.lng]
              : [7.90042, -72.50408]
          }
          zoom={form.location.lat ? 16 : 13}
          style={{ height: 200 }}
          className="rounded-xl overflow-hidden"
        >
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <MapFlyTo location={form.location} />
          <LocationPicker
            location={form.location}
            setLocation={(loc) => setForm({ ...form, location: loc })}
          />
        </MapContainer>
        {form.location.lat ? (
          <p className="text-xs text-green-600 mt-1">✓ Ubicación seleccionada ({Number(form.location.lat).toFixed(5)}, {Number(form.location.lng).toFixed(5)})</p>
        ) : (
          <p className="text-xs text-gray-400 mt-1">Toca el mapa para marcar tu ubicación o usa el botón de arriba</p>
        )}
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={form.isDefault}
          onChange={(e) => handleChange("isDefault", e.target.checked)}
        />
        Marcar como dirección por defecto
      </label>

      <div className="flex justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-xl border"
        >
          Cancelar
        </button>
        <button
          type="submit"
          className="px-4 py-2 rounded-xl bg-supernova-500 text-white font-semibold"
        >
          Guardar
        </button>
      </div>
    </form>
  );
}
